import { ButtonHTMLAttributes, forwardRef, FunctionComponent, ReactNode, SVGProps } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/utils/helpers";

type Icon = FunctionComponent<SVGProps<SVGSVGElement>>;

/*
 * Icon buttons (CreativeOS Library.lib.pen, Components / Buttons and actions,
 * style B). Circles. Ghost has no box: muted ink that rises to full ink on a
 * tinted circle. Close is a plain X with no ring. Primary is the solid accent.
 * Glass is the control-glass circle the arrows use, and the Day add plus.
 * Media is the clear glass over a picture or video. An icon-only action always
 * carries an accessible name; the hit area never drops below 30px except for
 * Day add, which sits beside a date.
 */
export type KitIconButtonKind = "ghost" | "close" | "primary" | "glass" | "media";
export type KitIconButtonSize = "xs" | "sm" | "md" | "lg" | "xl";

const KIND_CLASSES: Record<KitIconButtonKind, string> = {
  ghost: "text-co-muted not-disabled:hover:bg-co-hover not-disabled:hover:text-co-text",
  close: "text-co-muted not-disabled:hover:bg-co-hover not-disabled:hover:text-co-text",
  primary: "bg-co-accent-fill text-co-on-accent not-disabled:hover:bg-co-accent-fill-hover",
  glass: "co-glass-control text-co-text not-disabled:hover:bg-[color-mix(in_srgb,var(--co-glass-fill),var(--co-text)_6%)]",
  media: "co-glass-clear",
};

const DEFAULT_SIZE: Record<KitIconButtonKind, KitIconButtonSize> = {
  ghost: "sm",
  close: "md",
  primary: "lg",
  glass: "md",
  media: "xl",
};

/** Box, then glyph: Day add 26/15, Ghost 30/15, Close 32/14, Primary 36/16, Media 44/18. */
const SIZE_CLASSES: Record<KitIconButtonSize, { box: string; glyph: string }> = {
  xs: { box: "size-[26px]", glyph: "size-[15px]" },
  sm: { box: "size-[30px]", glyph: "size-[15px]" },
  md: { box: "size-8", glyph: "size-3.5" },
  lg: { box: "size-9", glyph: "size-4" },
  xl: { box: "size-11", glyph: "size-[18px]" },
};

export interface KitIconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  icon: Icon;
  "aria-label": string;
  kind?: KitIconButtonKind;
  size?: KitIconButtonSize;
  /** A pressed state the viewer set (sound on, saved): the accent dim fill. */
  pressed?: boolean;
  iconClassName?: string;
}

export function kitIconButtonClass(kind: KitIconButtonKind = "ghost", size?: KitIconButtonSize) {
  return cn(
    "relative inline-grid shrink-0 place-items-center rounded-full",
    "[transition:transform_var(--spring-snappy),background-color_160ms_ease,color_160ms_ease] not-disabled:active:scale-[0.97]",
    "disabled:cursor-not-allowed disabled:opacity-40",
    SIZE_CLASSES[size ?? DEFAULT_SIZE[kind]].box,
    KIND_CLASSES[kind],
  );
}

export const KitIconButton = forwardRef<HTMLButtonElement, KitIconButtonProps>(
  ({ icon: IconGlyph, kind = "ghost", size, pressed, className, iconClassName, type = "button", ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      aria-pressed={pressed}
      className={cn(kitIconButtonClass(kind, size), pressed && "bg-co-accent-dim text-co-text", className)}
      {...props}
    >
      <IconGlyph aria-hidden className={cn(SIZE_CLASSES[size ?? DEFAULT_SIZE[kind]].glyph, iconClassName)} />
    </button>
  ),
);

KitIconButton.displayName = "KitIconButton";

export interface KitTextActionProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: Icon;
  /** "accent" when the text action is the action to take (Emad, 1 Oct). */
  tone?: "muted" | "accent";
}

/** Text action: an icon and a word, no box. Muted, or the accent when it is the action to take. */
export const KitTextAction = forwardRef<HTMLButtonElement, KitTextActionProps>(
  ({ icon: IconGlyph, tone = "muted", children, className, type = "button", ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(
        "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-pill px-1 text-[13px] font-medium transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-40",
        tone === "accent" ? "text-co-accent not-disabled:hover:bg-co-accent-dim" : "text-co-muted not-disabled:hover:text-co-text",
        className,
      )}
      {...props}
    >
      {IconGlyph ? <IconGlyph aria-hidden className="size-3.5 shrink-0" /> : null}
      {children}
    </button>
  ),
);

KitTextAction.displayName = "KitTextAction";

/** A keyboard key, as the walk buttons and action tooltips print it. */
export function KitKeyCap({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <kbd
      className={cn(
        "inline-flex items-center rounded-[4px] bg-co-tonal px-[5px] py-px font-mono text-[10px] leading-[1.4] font-normal text-co-muted",
        className,
      )}
    >
      {children}
    </kbd>
  );
}

export interface KitWalkButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  direction: "previous" | "next";
  /** The key that does the same, printed in a key cap (K, J). */
  shortcut?: string;
}

/** Button / Walk: Previous or Next through a set, with its key. */
export const KitWalkButton = forwardRef<HTMLButtonElement, KitWalkButtonProps>(
  ({ direction, shortcut, children, className, type = "button", ...props }, ref) => {
    const Chevron = direction === "previous" ? ChevronLeft : ChevronRight;
    const chevron = <Chevron aria-hidden className="size-3.5 shrink-0" />;
    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          "inline-flex h-8 shrink-0 items-center gap-2 rounded-pill px-2.5 text-[13px] font-medium text-co-text2 transition-colors",
          "not-disabled:hover:bg-co-hover not-disabled:hover:text-co-text disabled:cursor-not-allowed disabled:opacity-40",
          className,
        )}
        {...props}
      >
        {direction === "previous" ? chevron : null}
        {children}
        {shortcut ? <KitKeyCap>{shortcut}</KitKeyCap> : null}
        {direction === "next" ? chevron : null}
      </button>
    );
  },
);

KitWalkButton.displayName = "KitWalkButton";

export interface KitMediaButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** A leading mark (a platform glyph) for the Connect capsule. */
  lead?: ReactNode;
}

/**
 * Media button / Connect: a 48px clear-glass capsule over a post's media, for
 * the one action the post needs (Connect TikTok, Try again, Choose audience).
 * For an icon-only control over media use KitIconButton kind="media".
 */
export const KitMediaButton = forwardRef<HTMLButtonElement, KitMediaButtonProps>(
  ({ lead, children, className, type = "button", ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(
        "co-glass-clear inline-flex h-12 shrink-0 items-center gap-2.5 rounded-pill px-5 text-[14px] font-semibold",
        "[transition:transform_var(--spring-snappy)] not-disabled:active:scale-[0.97] disabled:opacity-40",
        className,
      )}
      {...props}
    >
      {lead}
      {children}
    </button>
  ),
);

KitMediaButton.displayName = "KitMediaButton";
